import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, Quotes, PencilSimple } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SectionHead } from "@/components/site/Promotions";
import { api } from "@/lib/api";
import { toast } from "sonner";

const initial = { name: "", rating: 5, title: "", body: "", vehicle: "" };

export const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.title || !form.body) {
      toast.error("Please fill in your name, title and review");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", form);
      toast.success("Thanks! Your review is pending approval.");
      setForm(initial);
      setOpen(false);
      load();
    } catch {
      toast.error("Could not submit review");
    }
    setSubmitting(false);
  };

  const avg = reviews.length ? (reviews.reduce((a, r) => a + r.rating, 0) / reviews.length).toFixed(1) : "5.0";

  return (
    <section id="reviews" className="relative py-20 sm:py-28 bg-background z-10" data-testid="reviews">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <SectionHead index="04" kicker="Customer Reviews" title="What Drivers Say" />
          <div className="flex items-center gap-6">
            <div className="text-right">
              <div className="font-display font-black text-5xl text-primary leading-none">{avg}</div>
              <div className="flex items-center gap-0.5 justify-end mt-1">
                {[...Array(5)].map((_, i) => <Star key={i} size={14} weight="fill" className="text-primary" />)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">{reviews.length} verified reviews</div>
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button className="flex items-center gap-2 border border-primary text-primary font-bold text-sm uppercase tracking-wide px-5 py-3 rounded-sm hover:bg-primary hover:text-white transition-colors" data-testid="open-review-dialog">
                  <PencilSimple size={18} weight="bold" /> Write a review
                </button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border" data-testid="review-dialog">
                <DialogHeader>
                  <DialogTitle className="font-display font-black text-2xl uppercase tracking-tight">Share your experience</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4 mt-2" data-testid="review-form">
                  <div className="flex items-center gap-1" data-testid="rating-stars">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })} data-testid={`rating-${n}`}>
                        <Star size={30} weight={n <= form.rating ? "fill" : "regular"} className={n <= form.rating ? "text-primary" : "text-muted-foreground"} />
                      </button>
                    ))}
                  </div>
                  <Input placeholder="Your name *" value={form.name} onChange={set("name")} className="bg-background border-border rounded-sm h-11 focus-visible:ring-primary focus-visible:ring-1" data-testid="review-name" />
                  <Input placeholder="Vehicle (optional)" value={form.vehicle} onChange={set("vehicle")} className="bg-background border-border rounded-sm h-11 focus-visible:ring-primary focus-visible:ring-1" data-testid="review-vehicle" />
                  <Input placeholder="Review title *" value={form.title} onChange={set("title")} className="bg-background border-border rounded-sm h-11 focus-visible:ring-primary focus-visible:ring-1" data-testid="review-title" />
                  <Textarea placeholder="Tell us about it *" rows={4} value={form.body} onChange={set("body")} className="bg-background border-border rounded-sm focus-visible:ring-primary focus-visible:ring-1 resize-none" data-testid="review-body" />
                  <button type="submit" disabled={submitting} className="w-full bg-primary text-white font-bold py-3.5 rounded-sm hover:bg-red-500 transition-colors uppercase tracking-wide disabled:opacity-60" data-testid="review-submit">
                    {submitting ? "Posting..." : "Post review"}
                  </button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12" data-testid="reviews-grid">
          {reviews.map((r, i) => (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: Math.min(i * 0.06, 0.4) }}
              className="border border-border bg-card p-6 flex flex-col hover:border-primary/50 transition-colors"
              data-testid="review-card"
            >
              <Quotes size={28} weight="fill" className="text-primary/40" />
              <div className="flex items-center gap-0.5 mt-3">
                {[...Array(5)].map((_, s) => (
                  <Star key={s} size={14} weight="fill" className={s < r.rating ? "text-primary" : "text-muted"} />
                ))}
              </div>
              <h3 className="font-display font-bold text-lg mt-3 tracking-tight">{r.title}</h3>
              <p className="text-sm text-foreground/70 mt-2 leading-relaxed flex-1">{r.body}</p>
              <div className="mt-5 pt-4 border-t border-border flex items-center justify-between">
                <span className="text-sm font-bold">{r.name}</span>
                {r.vehicle && <span className="text-xs text-muted-foreground uppercase tracking-wide">{r.vehicle}</span>}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
